// TTD-specific transforms that shape a `launcherResult` JSON into the
// objects the shared CLW review grid + summary pane consume. Pure data —
// no React, no I/O. Mirrors dv360/dv360RowBuilders.js so the main component
// can dispatch by platform without per-platform JSX branching.
//
// Real TTD payload shape (from campaign-launcher / TTD API):
//   {
//     Platform: 'ttd',
//     Partner, AdvertiserId, AdvertiserName, Country, FiscalYear,
//     TimePeriod, Initiative, GeographyDetail,
//     Campaigns: [{ CampaignId, CampaignName, PrimaryChannel,
//                   Budget: {Amount, CurrencyCode}, BudgetInImpressions,
//                   DailyBudget, DailyBudgetInImpressions,
//                   StartDate, EndDate, PrimaryGoal, SecondaryGoal,
//                   TertiaryGoal, PacingMode, Objective,
//                   AdGroups: [adGroupId, …],   // ids only
//                   NewFrequencyConfigs: […] }],
//     AdGroups: [{ adGroupId, adGroupName, tactic, ChannelId,
//                  BaseBidCPM, MaxBidCPM, ROIGoal, PacingMode,
//                  DeviceType: ['PC', 'Mobile', …],
//                  AdEnvironment: ['Other', 'MobileOptimizedWeb', …],
//                  QualityViewability: {Display?: {Vendor, Min, Max}, Video?: {Vendor, Min, Max}},
//                  FunnelLocation,
//                  geography: { geographyId },
//                  MmgyAudiences: { ThirdPartyAudiences[], ResolvedAudiences[] },
//                  MmgyDeals: [{ SupplyVendorId, SupplyVendorDealId }, …],
//                  NewFrequencyConfigs: […],
//                  Description }],
//     CommonGeoLists: [{ id, TargetGeoSegmentIds: { GeoSegmentIds: [{ MetroName, CountryName }] } }],
//   }

import {
  ymdFromObj,
  deriveFormat,
  fmtCurrency,
} from '../clwFormatters';
import {
  ttdAmount,
  ttdCurrency,
  channelToFormat,
  pacingLabel,
  formatTtdGoal,
  formatFrequencyCap,
  supplyVendorName,
} from './ttdConstants';

const ymd = (s) => (typeof s === 'string' ? s.slice(0, 10) : ymdFromObj(s));

function viewabilityPercent(setting) {
  const raw = setting && typeof setting === 'object' ? setting.Min : setting;
  const value = Number(raw);
  if (!Number.isFinite(value)) return null;
  const percent = value <= 1 ? value * 100 : value;
  return Number.isInteger(percent) ? String(percent) : String(Number(percent.toFixed(2)));
}

// The v2 Adara/Sojern template carries independent Quality Alliance Display
// and Video ranges. The scalar form is the legacy Display-only shape.
export function formatTtdQualityViewability(adGroup) {
  const raw = adGroup?.QualityViewability ?? adGroup?.qualityViewability;
  if (raw == null) return '—';

  const settings = (raw && typeof raw === 'object' && !Array.isArray(raw))
    ? raw
    : { Display: raw };
  const labels = [];
  for (const dimension of ['Display', 'Video']) {
    const setting = settings[dimension] ?? settings[dimension.toLowerCase()];
    const percent = viewabilityPercent(setting);
    if (percent != null) labels.push(`QA ${dimension} ≥${percent}%`);
  }
  return labels.length ? labels.join(' · ') : '—';
}

// Reduce a TTD GeoSegmentIds list to human-friendly labels. Each segment
// can carry MetroName, CityName, RegionName, and/or CountryName — prefer
// the most specific name available, then dedupe so a list of 24 city-level
// segments that all share `CountryName: Canada` doesn't render as
// "Canada · Canada · …". A segment added through the grid's geo lookup
// carries the search result's flat `Name` instead (TTD's geosegment query
// returns GeoSegmentName, not the per-level name fields), so that's the last
// fallback — reviewEdits' ttdGeoLabel reads the same chain.
function labelsFromSegs(segs) {
  if (!Array.isArray(segs)) return [];
  const seen = new Set();
  const labels = [];
  for (const s of segs) {
    const label = s?.MetroName || s?.CityName || s?.RegionName || s?.CountryName || s?.Name;
    if (!label || seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }
  return labels;
}

// Look up the include + exclude geo lists for an ad group's geographyId.
// TargetGeoSegmentIds is the include list; BlockGeoSegmentIds is the
// exclude list. Both may be missing or empty.
function resolveGeoListById(geoListId, commonGeoLists) {
  if (!geoListId || !Array.isArray(commonGeoLists)) return { include: [], exclude: [] };
  const hit = commonGeoLists.find((g) => g.id === geoListId);
  const include = labelsFromSegs(hit?.TargetGeoSegmentIds?.GeoSegmentIds);
  const exclude = labelsFromSegs(hit?.BlockGeoSegmentIds?.GeoSegmentIds);
  return { include, exclude };
}

// TWO ad-group spellings reach these builders: the MMGY launch template
// (adGroupId/adGroupName, lowercase) and the campaign-COPY template
// (AdGroupId/AdGroupName — copy_ttd_campaign_launcher's curated shape, which
// the TTD creator reads capitalized). The copy grid used to alias before
// calling in, but the post-push CLW review page calls these builders
// DIRECTLY with the copy template, and every row rendered '—' names with no
// campaign linkage. Read both spellings here so every surface renders both
// shapes; MMGY templates carry the lowercase keys and are untouched.
const agIdOf = (ag) => ag?.adGroupId ?? ag?.AdGroupId;
const agNameOf = (ag) => ag?.adGroupName ?? ag?.AdGroupName;

// Ad-group tactic derived from the MMGY naming convention
// ("Audio_PMP_Retargeting_RT Site Visitor…", "Audio_Open_Lookalike_LAL…").
// Copy templates carry no `tactic` field — the source grid's tactic came from
// the IO — so the name is the only signal the copy carries. Display only.
export function deriveTtdTacticFromName(name) {
  const n = String(name || '').toLowerCase();
  if (!n) return null;
  if (n.includes('retarget') || /(^|[\s_-])rt([\s_-]|$)/.test(n)) return 'Retargeting';
  if (n.includes('lookalike') || /(^|[\s_-])lal([\s_-]|$)/.test(n)) return 'Lookalike';
  if (n.includes('added value') || /(^|[\s_-])av([\s_-]|$)/.test(n)) return 'Added Value';
  if (n.includes('prospect')) return 'Prospecting';
  return null;
}

// Headline numbers for the summary tiles.
export function summarizeTtdResult(result) {
  if (!result) return null;
  const campaigns = Array.isArray(result.Campaigns) ? result.Campaigns : [];
  const adGroups = Array.isArray(result.AdGroups) ? result.AdGroups : [];

  let totalImpressions = 0;
  let totalBudget = 0;
  for (const c of campaigns) {
    totalImpressions += Number(c.BudgetInImpressions || 0);
    const amt = ttdAmount(c.Budget);
    if (amt != null) totalBudget += amt;
  }
  const avgCpm = totalImpressions > 0 && totalBudget > 0 ? (totalBudget / totalImpressions) * 1000 : null;

  // Unique audience IDs across all ad groups (resolved 1P/3P). Copy templates
  // carry one already-merged TTD audience per ad group (ThirdPartyAudienceId)
  // instead of MmgyAudiences — count those so the tile doesn't claim
  // "No audience targeting" on a fully-targeted copy.
  const audSet = new Set();
  for (const ag of adGroups) {
    const resolved = ag?.MmgyAudiences?.ResolvedAudiences || [];
    for (const r of resolved) if (r.ThirdPartyDataId) audSet.add(r.ThirdPartyDataId);
    if (!resolved.length && ag?.ThirdPartyAudienceId) audSet.add(String(ag.ThirdPartyAudienceId));
  }

  const flightStart = ymd(campaigns[0]?.StartDate);
  const flightEnd = ymd(campaigns[0]?.EndDate);
  const ttdIssues = ttdIssueLists(result);

  return {
    platform: 'TTD',
    ioCount: campaigns.length,
    adGroupCount: adGroups.length,
    packageCount: 0,
    totalImpressions,
    totalBudget: totalBudget || null,
    avgCpm,
    flightStart,
    flightEnd,
    campaignName: campaigns[0]?.CampaignName || null,
    advertiserId: result.AdvertiserId || result.advertiserId || null,
    advertiserName: result.AdvertiserName || null,
    audienceCount: audSet.size,
    issueCount: ttdIssues.errors.length + ttdIssues.warnings.length,
    errorCount: ttdIssues.errors.length,
    warningCount: ttdIssues.warnings.length,
  };
}

// TTD parse issues (ttd_excel_to_json emits plain-string templateErrors /
// templateWarnings on the launcher result).
function ttdIssueLists(result) {
  return {
    errors: Array.isArray(result?.templateErrors) ? result.templateErrors.filter(Boolean) : [],
    warnings: Array.isArray(result?.templateWarnings) ? result.templateWarnings.filter(Boolean) : [],
  };
}

/**
 * Attach TTD templateErrors/templateWarnings to review rows as the same
 * annotations shape DV360/GT rows carry ({ field: { type: 'm'|'q', msg } }).
 * Messages are matched to the ad group they name; campaign-scoped messages
 * land on the campaign's first row (field 'market'); anything else lands on
 * the first row so no issue is silently dropped.
 */
function annotateTtdRows(rows, result) {
  const { errors, warnings } = ttdIssueLists(result);
  if (!rows.length || (!errors.length && !warnings.length)) return rows;

  const firstRowOfCampaign = new Map();
  for (const r of rows) {
    const key = String(r.market || '').toLowerCase();
    if (key && !firstRowOfCampaign.has(key)) firstRowOfCampaign.set(key, r);
  }

  const attach = (row, field, type, msg) => {
    const existing = row.annotations[field];
    if (!existing) {
      row.annotations[field] = { type, msg };
    } else {
      existing.extra = existing.extra || [];
      existing.extra.push({ type, msg });
    }
  };

  const place = (msg, type) => {
    const lower = String(msg).toLowerCase();
    const byAdGroup = rows.find((r) => r.name !== '—' && lower.includes(String(r.name).toLowerCase()));
    if (byAdGroup) { attach(byAdGroup, 'name', type, msg); return; }
    for (const [campaignName, row] of firstRowOfCampaign) {
      if (campaignName && lower.includes(campaignName)) { attach(row, 'market', type, msg); return; }
    }
    attach(rows[0], 'market', type, msg);
  };

  for (const msg of errors) place(msg, 'm');
  for (const msg of warnings) place(msg, 'q');
  return rows;
}

// One row per campaign for the summary-pane "By campaign" breakdown table.
export function breakdownTtdByCampaign(result) {
  if (!result) return [];
  const campaigns = Array.isArray(result.Campaigns) ? result.Campaigns : [];
  return campaigns.map((c) => {
    const fmt = channelToFormat(c.PrimaryChannel || '');
    const imps = Number(c.BudgetInImpressions || 0);
    const budget = ttdAmount(c.Budget);
    const rate = budget && imps ? (budget / imps) * 1000 : null;
    return {
      key: c.CampaignId || c.CampaignName,
      formatCode: fmt,
      packageName: c.CampaignName || '—',
      categoryLabel: c.PrimaryChannel || c.Objective || '—',
      lineItemCount: (c.AdGroups || []).length,
      impressions: imps,
      rate,
      budget,
      sizes: '—',
      flightStart: ymd(c.StartDate),
      flightEnd: ymd(c.EndDate),
    };
  });
}

// One row per ad group for the review grid.
export function buildTtdReviewRows(result) {
  if (!result) return [];
  const campaigns = Array.isArray(result.Campaigns) ? result.Campaigns : [];
  const adGroups = Array.isArray(result.AdGroups) ? result.AdGroups : [];
  const commonGeoLists = Array.isArray(result.CommonGeoLists) ? result.CommonGeoLists : [];
  // Copy builds attach a display-only sidecar keyed by source AdGroupId with
  // what lives inside the source's bid lists (deal ids, geo markets) — see
  // copy_ttd_campaign_launcher._build_review_enrichment. Absent on MMGY
  // launch templates, where the same facts ride MmgyDeals/CommonGeoLists.
  const enrichment = (result.review_enrichment && typeof result.review_enrichment === 'object')
    ? result.review_enrichment : {};
  const creativePlan = (result.creative_plan && typeof result.creative_plan === 'object')
    ? result.creative_plan : {};

  // Build adGroupId → parent campaign lookup so each row can pull dates +
  // primary channel from its campaign without re-scanning the list.
  const parentByAg = new Map();
  for (const c of campaigns) {
    for (const ref of (c.AdGroups || [])) parentByAg.set(ref, c);
  }

  const rows = adGroups.map((ag, idx) => {
    const agId = agIdOf(ag);
    const agName = agNameOf(ag);
    const parent = parentByAg.get(agId) || {};
    const sibs = (parent.AdGroups || []).length || 1;
    const ioImps = Number(parent.BudgetInImpressions || 0);
    const share = sibs > 0 ? ioImps / sibs : 0;
    const ioBudget = ttdAmount(parent.Budget);
    const rowBudget = ioBudget && sibs > 0 ? ioBudget / sibs : null;
    // The ADVERTISER'S currency, read off whichever money bag on this row states one.
    // Every 'cur' cell (budget, CPM, KPI target) formats through
    // fmtMoney(amount, row.currencyCode), which claims dollars when the row is silent —
    // so a EUR advertiser's ad group read as "$12 CPM" in both this grid and the copy
    // flow's Confirm & adjust step. Campaign budget first (the copy launcher always
    // stamps it: copy_ttd_campaign_launcher._build_campaign_and_adgroups), then the ad
    // group's own bid bags, then the ROI goal's.
    const currencyCode = ttdCurrency(parent.Budget)
      || ttdCurrency(parent.DailyBudget)
      || ttdCurrency(ag.BaseBidCPM)
      || ttdCurrency(ag.MaxBidCPM)
      || ttdCurrency(ag.ROIGoal && ag.ROIGoal[Object.keys(ag.ROIGoal)[0]])
      || null;

    const tactic = ag.tactic || deriveTtdTacticFromName(agName) || null;

    const fmt = channelToFormat(ag.ChannelId || parent.PrimaryChannel || '')
      || deriveFormat(agName, tactic);

    const baseBid = ttdAmount(ag.BaseBidCPM) ?? (typeof ag.BaseBidCPM === 'number' ? ag.BaseBidCPM : null);
    const maxBid = ttdAmount(ag.MaxBidCPM) ?? (typeof ag.MaxBidCPM === 'number' ? ag.MaxBidCPM : null);
    const cpm = baseBid;

    const dailyImps = Number(parent.DailyBudgetInImpressions || 0);
    const dailyCap = dailyImps ? Math.round(dailyImps / sibs) : null;

    const devices = Array.isArray(ag.DeviceType) && ag.DeviceType.length
      ? ag.DeviceType.join(', ')
      : '—';
    const environment = Array.isArray(ag.AdEnvironment) && ag.AdEnvironment.length
      ? ag.AdEnvironment.join(', ')
      : '—';

    const freqCap = formatFrequencyCap(ag.NewFrequencyConfigs);

    // Audiences: ResolvedAudiences[] are 1P/3P with taxonomy paths. Copy
    // templates instead carry the single already-merged TTD audience the
    // launch produced (ThirdPartyAudienceId) — truthful, so surface it.
    const resolved = ag?.MmgyAudiences?.ResolvedAudiences || [];
    const keywords3p = ag?.MmgyAudiences?.ThirdPartyAudiences || [];
    const audiences = resolved.length
      ? `${resolved.length} audience${resolved.length === 1 ? '' : 's'} · 3P resolved`
      : (ag.ThirdPartyAudienceId ? '1 audience · 3P resolved' : '—');
    const audienceList = resolved.map((r) => r.FullPath).filter(Boolean).join(' · ')
      || (ag.ThirdPartyAudienceId ? String(ag.ThirdPartyAudienceId) : '—');
    const keywords = keywords3p.length
      ? `${keywords3p.length} keyword${keywords3p.length === 1 ? '' : 's'}`
      : '—';
    const keywordList = keywords3p.length ? keywords3p.join(' · ') : '—';

    // Geo: resolve ad group geographyId via CommonGeoLists. We surface both
    // the include list (TargetGeoSegmentIds) and the exclude list
    // (BlockGeoSegmentIds) — the cell shows a compact include summary,
    // while the cell detail panel renders both sections in full. Copy
    // templates carry neither — their geo lives in the source's geo bid
    // lists, distilled into the review_enrichment sidecar server-side.
    const enrich = enrichment[String(agId)] || {};
    let { include: geoInc, exclude: geoExc } = resolveGeoListById(ag.geography?.geographyId, commonGeoLists);
    let geoUnresolved = 0;
    if (!geoInc.length && !geoExc.length) {
      geoInc = Array.isArray(enrich.geo_include) ? enrich.geo_include : [];
      geoExc = Array.isArray(enrich.geo_exclude) ? enrich.geo_exclude : [];
      geoUnresolved = Number(enrich.geo_unresolved || 0);
    }
    const geoIncludeList = geoInc.length ? geoInc.join(' · ') : '—';
    const geoExcludeList = geoExc.length ? geoExc.join(' · ') : '—';
    const geo = geoInc.length
      ? (geoExc.length ? `${geoInc.join(' · ')} · excl. ${geoExc.length}` : geoInc.join(' · '))
      : (geoUnresolved + geoExc.length > 0
        ? `${geoUnresolved + geoExc.length} geo segment${geoUnresolved + geoExc.length === 1 ? '' : 's'}`
        : (result.GeographyDetail || '—'));

    // KPI: ROIGoal is wrapped like { CPAInAdvertiserCurrency: { Amount } }.
    const goalKey = ag.ROIGoal ? Object.keys(ag.ROIGoal)[0] : null;
    const goalLabel = formatTtdGoal(ag.ROIGoal);
    const kpiType = goalKey ? goalKey.replace(/InAdvertiserCurrency$/, '').replace(/InPercent$/, '') : '—';
    const kpiTarget = goalLabel ? goalLabel.split(' · ')[1] || '—' : '—';

    // PMP / placement cell: combine the inventory type (PMP vs Open) with
    // the deal count from MmgyDeals. MarketplaceOptOut isn't reliable —
    // some accounts set it true on Open ad groups too — so detect PMP from
    // the name / tactic naming convention. Even when MmgyDeals is empty
    // (deals are often attached downstream) the inventory label still tells
    // reviewers what they're looking at.
    // Copy templates carry no MmgyDeals — their deals live in the source's
    // bid lists' BidLines, distilled into review_enrichment.deal_ids.
    const deals = Array.isArray(ag.MmgyDeals) && ag.MmgyDeals.length
      ? ag.MmgyDeals
      : (Array.isArray(enrich.deal_ids)
        ? enrich.deal_ids.map((id) => ({ SupplyVendorDealId: id }))
        : []);
    const nameLower = (agName || '').toLowerCase();
    const tacticLower = (ag.tactic || '').toLowerCase();
    const isPmp = nameLower.includes('pmp') || tacticLower.includes('pmp');
    const isOpen = /(^|[\s_-])open([\s_-]|$)/.test(nameLower) || /(^|[\s_-])open([\s_-]|$)/.test(tacticLower);
    const dealPart = deals.length ? `${deals.length} deal${deals.length === 1 ? '' : 's'}` : null;
    const inventoryLabel = isPmp ? 'PMP' : (isOpen ? 'Open exchange' : null);
    const pmp = inventoryLabel
      ? (dealPart ? `${inventoryLabel} · ${dealPart}` : inventoryLabel)
      : (dealPart || '—');
    // Full deal-id list for the cell detail panel. Each MmgyDeal entry is
    // { SupplyVendorId, SupplyVendorDealId, DealName? }. When the upstream
    // launcher includes DealName (e.g. "Xandr | CTV | RON | 30 sec | $25 | US")
    // we show it — that's the human-recognisable label reviewers compare
    // against. Otherwise fall back to "<SupplyVendor> — <UUID>" using the
    // local supplyVendorName lookup. Entries are joined by ` · ` so the
    // CellDetailPanel split-renderer puts each on its own line.
    const dealEntries = deals
      .map((d) => {
        const dealId = d?.SupplyVendorDealId || d?.supplyVendorDealId || '';
        const dealName = d?.DealName || d?.dealName || '';
        if (dealName) return dealName;
        if (!dealId) return '';
        const vendor = supplyVendorName(d?.SupplyVendorId ?? d?.supplyVendorId);
        return vendor ? `${vendor} — ${dealId}` : dealId;
      })
      .filter(Boolean);
    const dealList = dealEntries.length ? dealEntries.join(' · ') : '—';
    // Unique vendors across the deal list — used by the panel sub-label so a
    // reviewer can see at a glance whether the ad group is hitting one
    // exchange or multiple.
    const dealVendorSet = new Set(
      deals
        .map((d) => supplyVendorName(d?.SupplyVendorId ?? d?.supplyVendorId))
        .filter(Boolean)
    );
    const dealVendorSummary = dealVendorSet.size === 1
      ? [...dealVendorSet][0]
      : dealVendorSet.size > 1
        ? `${dealVendorSet.size} vendors`
        : null;

    return {
      rowId: agId || `ag-${idx}`,
      __refPath: `/AdGroups/${idx}`,
      idx: idx + 1,
      name: agName || '—',
      channel: 'TTD',
      currencyCode,
      fmt,
      market: parent.CampaignName || '—',
      package: parent.CampaignId || '—',
      category: ag.FunnelLocation || tactic || '—',
      tactic: tactic || '—',
      rawTactic: tactic || '—',
      start: ymd(parent.StartDate),
      end: ymd(parent.EndDate),
      flightType: 'Inherits campaign',
      budget: rowBudget,
      cpm,
      imps: share,
      dailyCap,
      // The divisor used just above for budget / imps / dailyCap. Published so
      // the cell editor can disclose the TOTAL a share edit will write to the
      // parent campaign before it is committed (see TTD_SCALED_FIELDS) — the
      // cell itself re-renders the share, so the total would otherwise never be
      // visible. Same value, not a recomputation.
      siblingCount: sibs,
      pacing: pacingLabel(ag.PacingMode || parent.PacingMode),
      geo,
      geoIncludeList,
      geoExcludeList,
      devices,
      environment,
      language: '—',
      age: '—',
      gender: '—',
      audiences,
      audienceList,
      keywords,
      keywordList,
      keywordCount: keywords3p.length,
      interests: '—',
      interestIds: [],
      expansion: ag?.MmgyAudiences?.ExpandWithThirdPartyAudienceSearch != null
        ? `Expand: ${ag.MmgyAudiences.ExpandWithThirdPartyAudienceSearch}`
        : '—',
      pmp,
      pmpDealList: dealList,
      pmpDealCount: deals.length,
      // Sub-label for the panel: "PMP — Pandora" when all 9 deals share one
      // vendor, "PMP — 2 vendors" when mixed, "PMP" / "Open exchange" when
      // there are no resolved vendors.
      pmpInventory: inventoryLabel
        ? (dealVendorSummary ? `${inventoryLabel} — ${dealVendorSummary}` : inventoryLabel)
        : '—',
      freqCap,
      // Copy pushes are shell launches whose creatives are RE-ASSOCIATED
      // post-create from creative_plan (same-advertiser ids, keyed by the
      // source AdGroupId) — surface that instead of implying "no creatives".
      creative: (ag.Creatives || []).length
        ? `${ag.Creatives.length} asset${ag.Creatives.length === 1 ? '' : 's'}`
        : ((creativePlan[String(agId)] || []).length
          ? `${creativePlan[String(agId)].length} creative${creativePlan[String(agId)].length === 1 ? '' : 's'} re-associated`
          : '—'),
      creativeFormat: '—',
      clickURL: '—',
      impTracker: 'TTD pixel',
      // TTD doesn't have a discrete "bid strategy" the way DV360 does — surface
      // the base/max CPM pair so reviewers see the bidding behaviour directly.
      bidStrategy: baseBid != null && maxBid != null ? `Base ${fmtCurrency(baseBid)} → Max ${fmtCurrency(maxBid)}` : '—',
      bidFloor: baseBid != null ? fmtCurrency(baseBid) : '—',
      bidCeiling: maxBid != null ? fmtCurrency(maxBid) : '—',
      viewability: formatTtdQualityViewability(ag),
      brandSafety: ag.MarketplaceOptOut ? 'Marketplace opt-out' : '—',
      pixel: 'None',
      kpi: kpiType,
      kpiTarget,
      convWindow: '—',
      bidGoal: goalLabel,
      // Copy templates state a status (every copied ad group is created
      // IsEnabled: false); MMGY parsed_response ad groups carry none.
      entityStatus: typeof ag.IsEnabled === 'boolean'
        ? (ag.IsEnabled ? 'ENABLED' : 'PAUSED')
        : '—',
      annotations: {},
    };
  });
  return annotateTtdRows(rows, result);
}

// Campaign-level configuration card on the summary pane. Aggregates across
// the campaigns + ad groups so the user can sanity-check the parsed plan
// at a glance.
export function extractTtdCampaignConfig(result) {
  if (!result) return {};
  const c = (result.Campaigns || [])[0];
  const adGroups = Array.isArray(result.AdGroups) ? result.AdGroups : [];
  const commonGeoLists = Array.isArray(result.CommonGeoLists) ? result.CommonGeoLists : [];

  const goal = formatTtdGoal(c?.PrimaryGoal) || '—';
  const kpiBits = [formatTtdGoal(c?.SecondaryGoal), formatTtdGoal(c?.TertiaryGoal)].filter(Boolean);
  const kpi = kpiBits.length ? kpiBits.join(' · ') : '—';

  // Geo summary — union of all include-target metros across ad groups.
  // Copy templates: geo rides the review_enrichment sidecar instead of
  // CommonGeoLists (see buildTtdReviewRows).
  const cfgEnrichment = (result.review_enrichment && typeof result.review_enrichment === 'object')
    ? result.review_enrichment : {};
  const geoSet = new Set();
  for (const ag of adGroups) {
    const { include } = resolveGeoListById(ag.geography?.geographyId, commonGeoLists);
    for (const name of include) geoSet.add(name);
    const enrichInc = cfgEnrichment[String(agIdOf(ag))]?.geo_include;
    if (Array.isArray(enrichInc)) for (const name of enrichInc) geoSet.add(name);
  }
  const geoSummary = geoSet.size === 0
    ? (result.GeographyDetail || result.Country || '—')
    : geoSet.size <= 4
      ? [...geoSet].join(', ')
      : `${geoSet.size} markets`;

  // Tactic and channel/format unions (name-derived for copy templates).
  const tacticSet = new Set();
  for (const ag of adGroups) {
    const t = ag.tactic || deriveTtdTacticFromName(agNameOf(ag));
    if (t) tacticSet.add(t);
  }
  const tacticSummary = tacticSet.size ? [...tacticSet].join(', ') : '—';

  const fmtSet = new Set();
  for (const c2 of (result.Campaigns || [])) if (c2.PrimaryChannel) fmtSet.add(channelToFormat(c2.PrimaryChannel));
  for (const ag of adGroups) if (ag.ChannelId) fmtSet.add(channelToFormat(ag.ChannelId));
  const formatSummary = fmtSet.size ? [...fmtSet].join(', ') : '—';

  // Pacing — most common across ad groups.
  const pacingCounts = {};
  for (const ag of adGroups) {
    const p = pacingLabel(ag.PacingMode);
    if (p) pacingCounts[p] = (pacingCounts[p] || 0) + 1;
  }
  const topPacing = Object.entries(pacingCounts).sort((a, b) => b[1] - a[1])[0]?.[0]
    || pacingLabel(c?.PacingMode)
    || '—';

  // Bid behaviour summary — typical (Base → Max) CPM across ad groups.
  const bidBands = adGroups
    .map((ag) => ({ base: ttdAmount(ag.BaseBidCPM) ?? ag.BaseBidCPM, max: ttdAmount(ag.MaxBidCPM) ?? ag.MaxBidCPM }))
    .filter((b) => Number.isFinite(Number(b.base)) && Number.isFinite(Number(b.max)));
  const topBid = bidBands.length
    ? `Base ${fmtCurrency(bidBands[0].base)} → Max ${fmtCurrency(bidBands[0].max)}`
    : '—';

  // Device union across ad groups.
  const deviceSet = new Set();
  for (const ag of adGroups) for (const d of (ag.DeviceType || [])) deviceSet.add(d);
  const deviceSummary = deviceSet.size ? [...deviceSet].join(', ') : 'All devices';

  const viewabilityLabels = [...new Set(
    adGroups
      .map((ag) => formatTtdQualityViewability(ag))
      .filter((label) => label !== '—'),
  )];
  const viewabilityTarget = viewabilityLabels.length === 0
    ? '—'
    : viewabilityLabels.length === 1
      ? viewabilityLabels[0]
      : 'Mixed';

  return {
    goal,
    kpi,
    freqCap: formatFrequencyCap(c?.NewFrequencyConfigs),
    advertiserId: result.AdvertiserId || result.advertiserId || null,
    viewabilityTarget,
    conversionPixels: 0,
    standardExclusions: 0,
    geoSummary,
    tacticSummary,
    formatSummary,
    budgetType: c?.Budget?.Amount != null ? 'Currency' : (c?.BudgetInImpressions ? 'Impressions' : '—'),
    topPacing,
    topBid,
    languageSummary: '—',
    deviceSummary,
    partnerId: result.Partner || null,
  };
}
